import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import StickyBottomNav from "../../components/Navbar/StickyBottomNav";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { ArrowLeft, Image, X, Maximize2 } from "lucide-react";
import "react-lazy-load-image-component/src/effects/blur.css";

const IndividualEventGallery = () => {
  const navigate = useNavigate();
  const { eventName } = useParams();
  const location = useLocation();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGallery, setShowGallery] = useState(false);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loadedImagesCount, setLoadedImagesCount] = useState(0);
  const [progress, setProgress] = useState(0);
  const folderId = location.state?.folderId;
  const apiUrl = process.env.REACT_APP_API_URL;

  // Function to preload an image using native browser Image
  const preloadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.onload = () => resolve();
      img.onerror = () => resolve(); // Resolve anyway to continue loading others
      img.src = url;
    });
  };

  useEffect(() => {
    if (folderId) {
      const fetchImages = async () => {
        try {
          const response = await fetch(
            `${apiUrl}/api/drive-photos?folderID=${folderId}&size=medium`
          );
          if (!response.ok) throw new Error("Network response was not ok");
          const data = await response.json();
          setImages(data);
          
          // Start tracking loading progress
          if (data.length > 0) {
            const totalImages = data.length;
            let loaded = 0;
            
            // Create an array to track loading status for each image
            const loadTracker = new Array(totalImages).fill(false);
            
            // Set up preloading for all images
            data.forEach((image, index) => {
              preloadImage(`${apiUrl}${image.thumbnailUrl}`)
                .then(() => {
                  // Mark this image as loaded
                  loadTracker[index] = true;
                  loaded = loadTracker.filter(Boolean).length;
                  
                  // Update state with new counts
                  setLoadedImagesCount(loaded);
                  const percentLoaded = Math.floor((loaded / totalImages) * 100);
                  setProgress(percentLoaded);
                  
                  // If 90% of images are loaded, show the gallery
                  if (percentLoaded >= 90 && !showGallery) {
                    setLoading(false);
                    setShowGallery(true);
                  }
                })
                .catch(err => {
                  // Count failed loads as "loaded" for progress purposes
                  loadTracker[index] = true;
                  loaded = loadTracker.filter(Boolean).length;
                  setLoadedImagesCount(loaded);
                  const percentLoaded = Math.floor((loaded / totalImages) * 100);
                  setProgress(percentLoaded);
                });
            });
            
            // Set a timeout as a fallback in case image loading takes too long
            setTimeout(() => {
              if (!showGallery) {
                setLoading(false);
                setShowGallery(true);
              }
            }, 8000); // 8 seconds fallback
          } else {
            setLoading(false);
            setShowGallery(true);
          }
          
        } catch (error) {
          console.error("Error fetching images:", error);
          setError("Failed to load images");
          setLoading(false);
          setShowGallery(true);
        }
      };
      fetchImages();
    } else {
      setLoading(false);
      setShowGallery(true);
      setError("Gallery not found. Please go back to the main gallery page.");
    }
  }, [folderId, apiUrl, showGallery]);

  const goBack = () => navigate("/gallery/events");

  return (
    <div className="min-h-screen bg-lightTeal">
      <Navbar />
      
      <div className="py-16 px-6 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <motion.button
              onClick={goBack}
              className="bg-primary text-white py-2 px-6 rounded-lg flex items-center shadow-sm"
              whileHover={{ x: -5 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <ArrowLeft size={20} className="mr-2" />
              Back to Event Gallery
            </motion.button>
          </div>
          
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-display text-h1 text-secondary mb-4">
              {eventName}
            </h1>
            <div className="w-16 h-1 bg-cta mx-auto"></div>
          </motion.div>
          
          {loading || !showGallery ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mb-6"></div>
              <p className="text-textPrimary mb-4">
                Loading gallery... {progress}%
              </p>
              <div className="w-full max-w-md bg-gray-200 rounded-full h-2.5 mb-2">
                <div 
                  className="bg-primary h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-500">
                {loadedImagesCount} of {images.length} images loaded
              </p>
            </div>
          ) : error ? (
            <motion.div 
              className="bg-white rounded-xl p-8 shadow-md max-w-md mx-auto text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4">
                <Image size={32} className="text-red-500" />
              </div>
              <h3 className="font-display text-h3 text-secondary mb-2">Something went wrong</h3>
              <p className="text-textPrimary mb-6">{error}</p>
              <motion.button
                onClick={goBack}
                className="bg-primary text-white py-2 px-6 rounded-lg flex items-center mx-auto"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ArrowLeft size={20} className="mr-2" />
                Return to Gallery
              </motion.button>
            </motion.div>
          ) : images.length === 0 ? (
            <motion.div 
              className="bg-white rounded-xl p-8 shadow-md max-w-md mx-auto text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="w-16 h-16 mx-auto rounded-full bg-lightTeal flex items-center justify-center mb-4">
                <Image size={32} className="text-primary" />
              </div>
              <h3 className="font-display text-h3 text-secondary mb-2">No Images Found</h3>
              <p className="text-textPrimary mb-6">This gallery doesn't contain any images yet.</p>
              <motion.button
                onClick={goBack}
                className="bg-primary text-white py-2 px-6 rounded-lg flex items-center mx-auto"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ArrowLeft size={20} className="mr-2" />
                Return to Gallery
              </motion.button>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="css-columns-masonry"
            >
              {images.map((image, index) => (
                <motion.div
                  key={image.id}
                  className="mb-4 bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg cursor-pointer break-inside-avoid"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.03 }}
                  whileHover={{ y: -5 }}
                  onClick={() => setSelectedImage(image)}
                >
                  <div className="overflow-hidden relative">
                    <LazyLoadImage
                      src={`${apiUrl}${image.thumbnailUrl}`}
                      alt={image.name}
                      className="w-full h-auto transition-transform duration-500 hover:scale-105"
                      effect="blur"
                      placeholderSrc="/placeholder-image.jpg"
                      afterLoad={() => {
                        // This is a backup for any lazy-loaded images that weren't preloaded
                        setLoadedImagesCount(prev => {
                          const newCount = prev + 1;
                          const percentLoaded = Math.floor((newCount / images.length) * 100);
                          setProgress(percentLoaded);
                          return newCount;
                        });
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-end justify-center">
                      <div className="p-4">
                        <Maximize2 size={24} className="text-white mx-auto" />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </div>
      
      {/* Image Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setSelectedImage(null)}
          >
            <motion.div 
              className="max-w-5xl w-full relative"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <motion.button
                className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/80 rounded-full w-10 h-10 flex items-center justify-center z-10"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImage(null);
                }}
              >
                <X size={24} />
              </motion.button>
              
              <div className="rounded-xl overflow-hidden shadow-2xl">
                <LazyLoadImage
                  src={`${apiUrl}${selectedImage.thumbnailUrl}`}
                  alt={selectedImage.name}
                  className="w-full h-auto max-h-[85vh] object-contain bg-black"
                  effect="blur"
                />
              </div>
              
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
                <div className="bg-black/70 backdrop-blur-sm text-white px-4 py-2 rounded-full">
                  {selectedImage.name}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* CSS for the masonry layout */}
      <style>{`
        .css-columns-masonry {
          column-count: 1;
          column-gap: 1rem;
        }
        
        @media (min-width: 640px) {
          .css-columns-masonry {
            column-count: 2;
          }
        }
        
        @media (min-width: 1024px) {
          .css-columns-masonry {
            column-count: 3;
          }
        }
        
        @media (min-width: 1280px) {
          .css-columns-masonry {
            column-count: 4;
          }
        }
        
        .break-inside-avoid {
          break-inside: avoid;
          display: inline-block;
          width: 100%;
        }
      `}</style>
      
      <Footer />
      <StickyBottomNav />
    </div>
  );
};

export default IndividualEventGallery;